import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, CircleAlert } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';
import { supabase } from '../../lib/supabase';

interface BlogPostPreview {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  cover_image: string;
  category: string;
  author: { name: string; avatar: string };
  read_time: number;
  created_at: string;
}

const MOCK_POSTS: BlogPostPreview[] = [
  {
    id: '1',
    title: 'La Révolution de la Mobilité Électrique en Afrique',
    slug: 'revolution-mobilite-electrique-afrique',
    excerpt:
      'Découvrez comment les motos électriques transforment le paysage urbain africain, réduisent les émissions et créent de nouvelles opportunités économiques.',
    content: "Contenu complet de l'article...",
    cover_image: 'https://images.pexels.com/photos/3819830/pexels-photo-3819830.jpeg',
    category: 'Innovation',
    author: { name: 'Dr. Amadou Diallo', avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg' },
    read_time: 8,
    created_at: new Date().toISOString(),
  },
  {
    id: '2',
    title: 'Impact Environnemental des Motos Électriques',
    slug: 'impact-environnemental-motos-electriques',
    excerpt:
      "Analyse détaillée de l'impact environnemental des motos électriques : réduction des émissions de CO2, qualité de l'air et développement durable.",
    content: "Contenu complet de l'article...",
    cover_image: 'https://images.pexels.com/photos/1108572/pexels-photo-1108572.jpeg',
    category: 'Environnement',
    author: { name: 'Dr. Fatou Ndiaye', avatar: 'https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg' },
    read_time: 10,
    created_at: new Date().toISOString(),
  },
  {
    id: '3',
    title: "Stations de Recharge Solaires : L'Innovation au Service de l'Autonomie",
    slug: 'stations-recharge-solaires-innovation-autonomie',
    excerpt:
      "Les stations de recharge solaires Proxy Power révolutionnent l'accès à l'énergie propre en Afrique tout en créant des emplois durables.",
    content: "Contenu complet de l'article...",
    cover_image: 'https://images.pexels.com/photos/9800002/pexels-photo-9800002.jpeg',
    category: 'Technologie',
    author: { name: 'Marie Koné', avatar: 'https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg' },
    read_time: 12,
    created_at: new Date().toISOString(),
  },
];

const BlogPreview: React.FC = () => {
  const [posts, setPosts] = useState<BlogPostPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    try {
      setError(null);
      setLoading(true);
      if (!supabase) {
        setPosts(MOCK_POSTS);
        return;
      }
      const { data, error: err } = await supabase.from('blog_posts').select('*').eq('is_active', true).order('created_at', { ascending: false }).limit(3);
      if (err) throw err;
      setPosts((data as BlogPostPreview[] | null) && (data as BlogPostPreview[]).length > 0 ? (data as BlogPostPreview[]) : MOCK_POSTS);
    } catch (e) {
      console.warn('Using mock blog posts due to network issues', e);
      setPosts(MOCK_POSTS);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  if (error) {
    return (
      <section className="py-14 sm:py-16 lg:py-20 bg-gradient-to-b from-white to-neutral-50/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
              <CircleAlert size={24} />
              <p className="text-lg font-medium">Error loading blog posts</p>
            </div>
            <p className="text-neutral-600">{error}</p>
            <button onClick={() => load()} className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-white relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 right-[8%] h-72 w-72 rounded-full bg-primary-500/[0.04] blur-3xl" />
      </div>
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col items-start gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
          <div>
            <SectionLabel number="13" className="mb-4">
              Le Journal Proxy
            </SectionLabel>
            <h2 className="text-3xl font-black leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-4xl lg:text-5xl">
              Insights & <span className="text-primary-600">vision</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-600 sm:text-base">
              Analyses, prospective et thought leadership signés par notre équipe — sur la mobilité électrique africaine.
            </p>
          </div>
          <Link
            to="/blog"
            className="group hidden shrink-0 items-center gap-2 rounded-xl border border-neutral-300 bg-white px-5 py-3 text-sm font-bold text-neutral-900 transition-all duration-300 hover:-translate-y-0.5 hover:border-neutral-900 hover:shadow-[0_14px_30px_-12px_rgba(0,0,0,0.15)] sm:inline-flex"
          >
            Tous nos insights
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
            {[1, 2, 3].map((c) => (
              <div key={c} className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                  <div className="h-8 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:gap-8 md:overflow-visible md:pb-0"
            style={{ scrollPaddingLeft: '1rem' }}
          >
            {posts.map((post) => (
              <article
                key={post.id}
                className="shrink-0 snap-center w-[80%] md:w-auto md:shrink bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:-translate-y-2 hover:shadow-xl group"
                onMouseEnter={() => setHovered(post.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="relative overflow-hidden">
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      className={`w-full h-48 object-cover transition-transform duration-700 ${hovered === post.id ? 'scale-110' : 'scale-100'}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <span className="flex items-center">
                        <Calendar size={16} className="mr-1" />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center">
                        <Clock size={16} className="mr-1" />
                        {post.read_time} min
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">{post.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <span className="inline-flex items-center text-primary-600 font-medium group-hover:translate-x-1 transition-transform">
                      Lire la suite
                      <ArrowRight size={16} className="ml-1" />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}

        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/blog"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-5 py-3 text-sm font-bold text-neutral-900 transition-all active:scale-95"
          >
            Tous nos insights
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
